<template>
	<teleport to="body">
		<div v-if="display" class="notification-content">
			<transition-group
				name="notification-stack"
				tag="div"
				appear
				role="status"
				aria-live="polite"
				aria-relevant="additions"
				class="d-flex flex-column-reverse ga-2 align-start notification-group"
				@before-leave="pinLeavingCard"
				@after-leave="hideIfEmpty"
			>
				<div
					v-for="(notif, index) in visible"
					:key="notif.id"
					class="notification-item"
				>
					<v-alert
						:model-value="true"
						:color="color[notif.status] ?? 'info'"
						:class="[
							'notification-alert',
							{ 'top-card': isTopCardWithQueue(index) },
						]"
						@update:model-value="dismissNotification(notif)"
					>
						<span class="text-white text-body-2">{{ notif.message }}</span>
						<template #close="{ props }">
							<v-btn
								v-bind="props"
								icon="mdi-close"
								size="small"
								variant="text"
								class="text-white mr-1"
								aria-label="Dismiss notification"
							/>
						</template>
					</v-alert>
					<transition name="notification-label">
						<div
							v-if="isTopCardWithQueue(index)"
							class="notifications-label text-caption font-weight-medium"
							:class="`text-${color[notif.status] ?? 'info'}`"
						>
							+{{ queue.length }} more
						</div>
					</transition>
				</div>
			</transition-group>
		</div>
	</teleport>
</template>

<script>
import { watch } from "vue";
import { mapActions } from "pinia";
import { useMainStore } from "@/store";
import { v4 as uuidv4 } from "uuid";

export default {
	name: "VNotification",

	data() {
		return {
			color: {
				200: "success",
				401: "error",
				404: "error",
				error: "error",
				info: "info",
				success: "success",
				warning: "warning",
				partial: "warning",
			},
			display: false,
			queue: [],
			visible: [],
			n: 3,
		};
	},
	created() {
		this.timers = new Map(); // links notif uuid to timer object
	},
	mounted() {
		// watch the store notification value for changes
		const store = useMainStore();
		watch(
			() => store.notification,
			({
				message,
				status = "info",
				persist = false,
				timeout = null,
				forceClear = false,
			}) => {
				if (message && status) {
					// clone the notification
					const notification = {
						message,
						status,
						persist,
						timeout,
						id: uuidv4(),
					};

					// clear store
					this.clearNotification();

					// set timeout for specific notification so queued notifs have valid timeouts
					notification.timeout = this.getTimeout(notification);

					if (this.visible.length >= this.n) {
						// push into queue if currently displaying
						this.queue.push(notification);
					} else {
						// add notification to visible stack
						this.displayNotification(notification);
					}
				} else if (forceClear) {
					this.clearNotification();
					this.forceClearNotifications();
				}
			},
			{ deep: true },
		);
	},
	beforeUnmount() {
		this.timers.forEach(clearTimeout);
		this.timers.clear();
	},
	methods: {
		...mapActions(useMainStore, ["clearNotification"]),
		displayNotification(notification) {
			this.display = true;
			this.visible.push(notification);

			if (notification.timeout >= 0) {
				this.timers.set(
					notification.id,
					setTimeout(() => {
						this.dismissNotification(notification);
					}, notification.timeout),
				);
			}
		},
		dismissNotification(notification) {
			clearTimeout(this.timers.get(notification.id));
			this.timers.delete(notification.id);
			const index = this.visible.indexOf(notification);
			if (index >= 0) {
				this.visible.splice(index, 1);

				// load next queued notification
				if (this.queue.length > 0 && this.visible.length < this.n) {
					const next = this.queue.shift();
					if (next) {
						this.displayNotification(next);
					}
				}
			}
		},
		forceClearNotifications() {
			// for each notif in visible, clear if not persistent and not error
			const notifsForRemoval = this.visible.filter((notification) =>
				this.isForceClearable(notification),
			);
			for (const notification of notifsForRemoval) {
				this.dismissNotification(notification);
			}
		},
		pinLeavingCard(el) {
			// prevent notification stack container from snapping to 0 width
			// upon dismissal of final notification
			const bottom =
				el.parentElement.clientHeight - (el.offsetTop + el.offsetHeight);
			// prevent text wrapping upon dismissal due to rounding
			const width = el.getBoundingClientRect().width;
			el.style.bottom = `${bottom}px`;
			el.style.left = `${el.offsetLeft}px`;
			el.style.width = `${width}px`;
		},
		hideIfEmpty() {
			if (this.visible.length === 0) {
				this.display = false;
			}
		},
		getTimeout({ status, persist, timeout }) {
			if (persist) {
				return -1;
			} else if (timeout) {
				return timeout;
			}
			return this.color[status] === "error" ? -1 : 3000;
		},
		isTopCardWithQueue(index) {
			// if index is newest visible notification card and queue is not empty
			return index === this.n - 1 && this.queue.length !== 0;
		},
		isForceClearable(notification) {
			// is not persistent and has non-negative finite timeout
			return !notification.persist && notification.timeout > -1;
		},
	},
};
</script>

<style scoped lang="scss">
.notification-content {
	z-index: 10000;
	position: fixed;
	left: 16px;
	bottom: 16px;
}

.notification-group {
	position: relative;
	isolation: isolate;
}

.notification-item {
	position: relative;
}

.notifications-label {
	position: absolute;
	top: -10px; /* half inside the card, over the faded zone */
	left: 50%;
	transform: translateX(-50%);
	z-index: 1;
	width: fit-content;
	white-space: nowrap;
	padding: 2px 10px;
	border-radius: 999px;
	background: rgb(var(--v-theme-surface));
	box-shadow: 0 1px 4px rgb(0 0 0 / 0.25);
	pointer-events: none; /* don't block the card's close button */
}

@property --fade-strength {
	syntax: "<number>";
	inherits: false;
	initial-value: 0;
}

.notification-alert {
	min-width: 344px;
	max-width: 672px;
	padding: 14px 8px 14px 16px;
	min-height: 48px;
	font-size: 0.875rem;
	line-height: 1.425;
	letter-spacing: 0.0178571429em;
	pointer-events: auto;

	--fade-strength: 0;
	transition: --fade-strength 0.15s ease;
	--fade-height: 25px;
	--fade: linear-gradient(
		to bottom,
		hsl(0 0% 0% / calc(1 - 0.8 * var(--fade-strength))) 0,
		hsl(0 0% 0% / calc(1 - 0.77 * var(--fade-strength)))
			calc(var(--fade-height) * 0.1),
		hsl(0 0% 0% / calc(1 - 0.72 * var(--fade-strength)))
			calc(var(--fade-height) * 0.225),
		hsl(0 0% 0% / calc(1 - 0.62 * var(--fade-strength)))
			calc(var(--fade-height) * 0.35),
		hsl(0 0% 0% / calc(1 - 0.49 * var(--fade-strength)))
			calc(var(--fade-height) * 0.475),
		hsl(0 0% 0% / calc(1 - 0.35 * var(--fade-strength)))
			calc(var(--fade-height) * 0.6),
		hsl(0 0% 0% / calc(1 - 0.21 * var(--fade-strength)))
			calc(var(--fade-height) * 0.725),
		hsl(0 0% 0% / calc(1 - 0.1 * var(--fade-strength)))
			calc(var(--fade-height) * 0.825),
		hsl(0 0% 0% / calc(1 - 0.02 * var(--fade-strength)))
			calc(var(--fade-height) * 0.925),
		hsl(0 0% 0% / 1) var(--fade-height)
	);
	mask-image: var(--fade);
	-webkit-mask-image: var(--fade);

	:deep(.v-alert__close) {
		grid-row: 1 / -1;
		align-self: center;
		margin-inline-start: 8px;
		margin-block: -10px;
	}
}

.top-card {
	--fade-strength: 1.1;
}

.notification-label {
	&-enter-active,
	&-leave-active {
		transition: opacity 0.15s cubic-bezier(0, 0, 0.2, 1);
	}

	&-leave-active {
		z-index: -1;
	}

	&-enter-from,
	&-leave-to {
		opacity: 0;
	}
}

/* mimic transition animations of v-snackbar */
.notification-stack {
	&-enter-active,
	&-leave-active {
		transition-duration: 0.15s;
		transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
	}

	&-enter-active {
		transition-property: opacity, transform;
	}

	&-leave-active {
		transition-property: opacity;
		position: absolute;
		z-index: -1; /* render behind cards that remain */
	}

	&-enter-from {
		opacity: 0;
		transform: scale(0.8);
	}

	&-leave-to {
		opacity: 0;
	}

	&-move {
		transition: transform 0.15s cubic-bezier(0, 0, 0.2, 1);
	}
}
</style>
